import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  Pagination,
  Alert,
} from "@mui/material";
import { Add, Search, Refresh } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Coach } from "../types/Coach";
import { coachesApi } from "../services/coachesApi";
import CoachesTable from "../components/CoachesTable";
import CoachForm from "../components/CoachForm";
import CoachDetails from "../components/CoachDetails";
import DeleteCoachDialog from "../components/DeleteCoachDialog";

const CoachesPage: React.FC = () => {
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCoaches, setTotalCoaches] = useState(0);
  const [error, setError] = useState("");

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const limit = 10;

  useEffect(() => {
    const loadCoaches = async () => {
      try {
        if (!token) {
          toast.error("Vous devez vous connecter pour accéder à cette page");
          navigate("/login");
          return;
        }

        setLoading(true);
        setError("");

        const response = await coachesApi.getAll(
          currentPage,
          limit,
          searchTerm
        );

        console.log("response :", response);

        setCoaches(response.data.coaches);
        setTotalPages(response.data.totalPages);
        setTotalCoaches(response.results);
        console.log("response.results :", response.results);
      } catch (err: any) {
        console.error("Error fetching coaches:", err);
        if (err.response?.status === 401) {
          toast.error("Session expirée, veuillez vous reconnecter");
          logout();
          navigate("/login");
        } else {
          setError("Erreur lors du chargement des entraîneurs");
          toast.error("Erreur lors du chargement des entraîneurs");
        }
      } finally {
        setLoading(false);
      }
    };

    loadCoaches();
  }, [currentPage, searchTerm, token, logout, navigate]);

  const fetchCoaches = async () => {
    try {
      if (!token) {
        toast.error("Vous devez vous connecter pour accéder à cette page");
        navigate("/login");
        return;
      }

      setLoading(true);
      setError("");

      const response = await coachesApi.getAll(currentPage, limit, searchTerm);

      setCoaches(response.data.coaches);
      setTotalPages(response.data.totalPages);
      setTotalCoaches(response.data.totalCoaches);
    } catch (err: any) {
      console.error("Error fetching coaches:", err);
      if (err.response?.status === 401) {
        toast.error("Session expirée, veuillez vous reconnecter");
        logout();
        navigate("/login");
      } else {
        setError("Erreur lors du chargement des entraîneurs");
        toast.error("Erreur lors du chargement des entraîneurs");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleView = (coach: Coach) => {
    setSelectedCoach(coach);
    setDetailsOpen(true);
  };

  const handleEdit = (coach: Coach) => {
    setSelectedCoach(coach);
    setFormOpen(true);
  };

  const handleDelete = (coach: Coach) => {
    setSelectedCoach(coach);
    setDeleteOpen(true);
  };

  const handleAdd = () => {
    setSelectedCoach(null);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    fetchCoaches();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCoach) return;

    setDeleteLoading(true);
    try {
      await coachesApi.delete(selectedCoach._id);
      toast.success("Entraîneur supprimé avec succès");
      setDeleteOpen(false);
      fetchCoaches();
    } catch (err: any) {
      console.error("Error deleting coach:", err);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchCoaches();
    toast.info("Liste actualisée");
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ mb: 4, color: "#2c3e50", fontWeight: "bold" }}
      >
        Gestion des Entraîneurs
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total des entraîneurs
            </Typography>
            <Typography variant="h4" component="div" color="primary">
              {totalCoaches}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Entraîneurs actifs
            </Typography>
            <Typography variant="h4" component="div" color="success">
              {coaches.filter((coach) => coach.isActive).length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Controls */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          placeholder="Rechercher un entraîneur..."
          value={searchTerm}
          onChange={handleSearch}
          variant="outlined"
          size="small"
          sx={{ flex: 1, minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={loading}
          startIcon={<Refresh />}
          sx={{ textTransform: "none" }}
        >
          Actualiser
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          startIcon={<Add />}
          sx={{
            textTransform: "none",
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
            },
          }}
        >
          Ajouter un entraîneur
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : coaches.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm
                ? "Aucun entraîneur trouvé"
                : "Aucun entraîneur enregistré"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm
                ? "Essayez de modifier vos critères de recherche"
                : "Commencez par ajouter votre premier entraîneur"}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                onClick={handleAdd}
                startIcon={<Add />}
                sx={{
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                }}
              >
                Ajouter un entraîneur
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <CoachesTable
            coaches={coaches}
            loading={loading}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Dialogs */}
      <CoachForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        coach={selectedCoach}
        onSuccess={handleFormSuccess}
      />

      <CoachDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        coach={selectedCoach}
      />

      <DeleteCoachDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        coach={selectedCoach}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default CoachesPage;
